import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BRAND = "#0047d6";
const BRAND_CYAN = "#45b2ff";

function pct(n) {
  if (!n && n !== 0) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function KpiCard({ label, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-extrabold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function TierColumn({ tier, data, label }) {
  const conv = data.quizzes ? (data.checkouts + data.leads) / data.quizzes : 0;
  return (
    <div data-testid={`tier-col-${tier}`} className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="text-xs font-extrabold uppercase tracking-widest text-slate-500">{label}</div>
      <dl className="mt-3 space-y-1.5 text-sm">
        <div className="flex justify-between"><dt className="text-slate-500">Quizzes</dt><dd className="font-semibold text-slate-900">{data.quizzes}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">Checkouts</dt><dd className="font-semibold text-blue-700">{data.checkouts}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">Leads</dt><dd className="font-semibold text-amber-700">{data.leads}</dd></div>
        <div className="flex justify-between border-t border-slate-100 pt-1.5"><dt className="text-slate-500">Conversion</dt><dd className="font-semibold text-slate-900">{pct(conv)}</dd></div>
      </dl>
    </div>
  );
}

function SecretGate({ onSubmit, error }) {
  const [s, setS] = useState("");
  return (
    <div className="mx-auto mt-24 max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="secret-gate">
      <div
        className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.22em]"
        style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
      >
        Starpower Network
      </div>
      <h1 className="text-xl font-extrabold">Sizer Analytics</h1>
      <p className="mt-1 text-sm text-slate-500">Enter the admin secret.</p>
      <input
        data-testid="secret-input"
        type="password"
        value={s}
        onChange={(e) => setS(e.target.value)}
        placeholder="ADMIN_SECRET"
        className="mt-4 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
        onKeyDown={(e) => { if (e.key === "Enter") onSubmit(s); }}
      />
      {error && <div data-testid="secret-error" className="mt-2 text-xs text-red-600">{error}</div>}
      <button
        data-testid="secret-submit"
        onClick={() => onSubmit(s)}
        className="mt-4 w-full rounded-md px-4 py-2 text-sm font-semibold text-white"
        style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
      >
        Continue →
      </button>
    </div>
  );
}

export default function AdminDashboard() {
  const [secret, setSecret] = useState(() => {
    try { return sessionStorage.getItem("sizer_admin_secret") || ""; } catch (_) { return ""; }
  });
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [retryStatus, setRetryStatus] = useState(null);
  const [expandedNote, setExpandedNote] = useState(null);

  const load = useCallback(async (s, d) => {
    setLoading(true);
    setAuthError(null);
    try {
      const [sumResp, leadsResp, consultsResp] = await Promise.all([
        axios.get(`${API}/admin/analytics/summary`, { params: { secret: s, days: d } }),
        axios.get(`${API}/admin/leads`, { params: { secret: s } }),
        axios.get(`${API}/admin/consultations`, { params: { secret: s } }),
      ]);
      setSummary(sumResp.data);
      setLeads((leadsResp.data.leads || []).slice(0, 50));
      setConsultations((consultsResp.data.consultations || []).slice(0, 50));
      try { sessionStorage.setItem("sizer_admin_secret", s); } catch (_) { /* no-op */ }
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) {
        setAuthError("Invalid admin secret.");
        try { sessionStorage.removeItem("sizer_admin_secret"); } catch (_) { /* no-op */ }
        setSecret("");
      } else {
        setAuthError(e?.response?.data?.detail || e.message);
      }
      setSummary(null);
      setLeads([]);
      setConsultations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (secret) load(secret, days);
  }, [secret, days, load]);

  async function bulkRetry() {
    setRetryStatus("running");
    try {
      const r = await axios.post(`${API}/admin/leads/retry-ghl`, null, { params: { secret } });
      setRetryStatus(`Retried ${r.data.retried} lead(s).`);
      await load(secret, days);
    } catch (e) {
      setRetryStatus(`Error: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function forceSyncOne(leadId) {
    setRetryStatus("running");
    try {
      const r = await axios.post(`${API}/admin/leads/${leadId}/force-sync`, null, { params: { secret } });
      setRetryStatus(`Force-synced ${r.data.lead_id}`);
      await load(secret, days);
    } catch (e) {
      setRetryStatus(`Error: ${e?.response?.data?.detail || e.message}`);
    }
  }

  async function forceSyncConsult(consultId) {
    setRetryStatus("running");
    try {
      const r = await axios.post(`${API}/admin/consultations/${consultId}/force-sync`, null, { params: { secret } });
      setRetryStatus(`Force-synced consultation ${r.data.consult_id}`);
      await load(secret, days);
    } catch (e) {
      setRetryStatus(`Error: ${e?.response?.data?.detail || e.message}`);
    }
  }

  function downloadCsv() {
    const url = `${API}/admin/leads.csv?secret=${encodeURIComponent(secret)}&limit=10000`;
    window.open(url, "_blank");
  }

  function logout() {
    try { sessionStorage.removeItem("sizer_admin_secret"); } catch (_) { /* no-op */ }
    setSecret("");
    setSummary(null);
    setLeads([]);
  }

  if (!secret) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SecretGate
          error={authError}
          onSubmit={(s) => { if (s) setSecret(s); }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-6 py-4">
          <div>
            <div
              className="text-[11px] font-extrabold uppercase tracking-[0.22em]"
              style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}
            >Starpower Network</div>
            <h1 className="text-xl font-extrabold">Sizer Analytics</h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              data-testid="shop-cta"
              href="https://shop.starpower.network"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full px-3 py-1.5 text-xs font-semibold text-white shadow-sm sm:text-sm"
              style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
            >Shop at Starpower ↗</a>
            <select
              data-testid="range-select"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 px-6 py-8">
        {authError && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{authError}</div>}
        {loading && <div className="text-sm text-slate-500">Loading…</div>}

        {summary && (
          <>
            {/* KPI row */}
            <section className="grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="kpi-row">
              <KpiCard label="Quizzes" value={summary.totals.quizzes_completed} sub={`Last ${days} days`} testid="kpi-quizzes" />
              <KpiCard label="Checkouts" value={summary.totals.checkouts_started} sub={`${pct(summary.totals.checkout_rate)} conv`} testid="kpi-checkouts" />
              <KpiCard label="Leads" value={summary.totals.leads_captured} sub={`${pct(summary.totals.lead_rate_when_blocked)} when blocked`} testid="kpi-leads" />
              <KpiCard label="Consults" value={summary.totals.consults_requested ?? 0} sub="Whole-home design" testid="kpi-consults" />
              <KpiCard label="Shares" value={summary.totals.shares_created} sub="Sized → shared" testid="kpi-shares" />
            </section>

            {/* Tier funnel */}
            <section>
              <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-500">Tier funnel</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="tier-funnel">
                <TierColumn tier="good" label="Good" data={summary.tier_breakdown.good} />
                <TierColumn tier="better" label="Better (recommended)" data={summary.tier_breakdown.better} />
                <TierColumn tier="best" label="Best" data={summary.tier_breakdown.best} />
              </div>
            </section>

            {/* Daily series chart */}
            <section className="rounded-2xl border border-slate-200 bg-white p-5">
              <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-500">Daily activity</h2>
              <div className="h-64" data-testid="daily-chart">
                {summary.daily_series.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-slate-400">No events yet in this window.</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={summary.daily_series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="quizzes" stroke={BRAND} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="checkouts" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="leads" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </section>

            {/* Two-column: top appliances + top models */}
            <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-500">Top appliances</h3>
                {summary.top_appliances.length === 0 ? <div className="text-xs text-slate-400">No data yet.</div> : (
                  <ul className="space-y-2" data-testid="top-appliances">
                    {summary.top_appliances.map((a) => {
                      const max = summary.top_appliances[0]?.count || 1;
                      const w = `${Math.max(8, Math.round((a.count / max) * 100))}%`;
                      return (
                        <li key={a.key} className="text-sm">
                          <div className="flex justify-between"><span className="text-slate-700">{a.label}</span><span className="font-semibold text-slate-900">{a.count}</span></div>
                          <div className="mt-1 h-2 rounded bg-slate-100"><div className="h-2 rounded" style={{ width: w, background: `linear-gradient(90deg, ${BRAND}, ${BRAND_CYAN})` }} /></div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-500">Top models recommended</h3>
                {summary.top_models_recommended.length === 0 ? <div className="text-xs text-slate-400">No data yet.</div> : (
                  <ul className="space-y-2" data-testid="top-models">
                    {summary.top_models_recommended.map((m) => (
                      <li key={m.model} className="flex justify-between text-sm">
                        <span className="text-slate-700">{m.model}</span><span className="font-semibold text-slate-900">{m.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </>
        )}

        {/* Consultations table */}
        <section className="rounded-2xl border border-slate-200 bg-white" data-testid="consultations-section">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Recent consultations · Ocean Pro / whole-home</h2>
            <span className="text-xs text-slate-500">{consultations.length} shown</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="consults-table">
              <thead className="bg-amber-50 text-left text-[11px] uppercase tracking-wider text-amber-800">
                <tr>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Phone</th>
                  <th className="px-4 py-2">Tier</th>
                  <th className="px-4 py-2">Model</th>
                  <th className="px-4 py-2">GHL</th>
                  <th className="px-4 py-2">Notes</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {consultations.map((c) => {
                  const fullNote = c.notes || "";
                  const truncated = fullNote.length > 60 ? fullNote.slice(0, 60) + "…" : fullNote;
                  const isOpen = expandedNote === c.consult_id;
                  return (
                    <tr key={c.consult_id} className="border-t border-slate-100" data-testid={`consult-row-${c.consult_id}`}>
                      <td className="px-4 py-2 text-xs text-slate-500">{(c.created_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="px-4 py-2">{c.email}</td>
                      <td className="px-4 py-2 text-xs">{c.phone || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-2 capitalize">{c.tier}</td>
                      <td className="px-4 py-2">{c.model}</td>
                      <td className="px-4 py-2">{c.ghl_synced ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">✗</span>}</td>
                      <td className="px-4 py-2 text-xs">
                        {fullNote ? (
                          <button
                            data-testid={`consult-note-${c.consult_id}`}
                            onClick={() => setExpandedNote(isOpen ? null : c.consult_id)}
                            className="text-left text-slate-600 hover:text-blue-700"
                          >
                            {isOpen ? fullNote : truncated}
                          </button>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          data-testid={`retry-consult-${c.consult_id}`}
                          onClick={() => forceSyncConsult(c.consult_id)}
                          className="text-xs font-semibold text-amber-700 hover:underline"
                        >Retry GHL</button>
                      </td>
                    </tr>
                  );
                })}
                {consultations.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">No consultations yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {/* Leads table */}
        <section className="rounded-2xl border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-5 py-4">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">Recent leads</h2>
            <div className="flex flex-wrap items-center gap-2">
              {retryStatus && <span className="text-xs text-slate-500" data-testid="retry-status">{retryStatus}</span>}
              <button
                data-testid="bulk-retry-btn"
                onClick={bulkRetry}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >Retry GHL syncs</button>
              <button
                data-testid="download-csv-btn"
                onClick={downloadCsv}
                className="rounded-md px-3 py-1.5 text-xs font-semibold text-white"
                style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
              >Download CSV</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2">Created</th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Tier</th>
                  <th className="px-4 py-2">Model</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">GHL</th>
                  <th className="px-4 py-2">Retries</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.lead_id} className="border-t border-slate-100" data-testid={`lead-row-${l.lead_id}`}>
                    <td className="px-4 py-2 text-xs text-slate-500">{(l.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-4 py-2">{l.email}</td>
                    <td className="px-4 py-2 capitalize">{l.tier}</td>
                    <td className="px-4 py-2">{l.model}</td>
                    <td className="px-4 py-2"><span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{l.status}</span></td>
                    <td className="px-4 py-2">{l.ghl_synced ? <span className="text-emerald-600">✓</span> : <span className="text-slate-400">✗</span>}</td>
                    <td className="px-4 py-2 tabular-nums text-slate-600">{l.retry_count || 0}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        data-testid={`retry-${l.lead_id}`}
                        onClick={() => forceSyncOne(l.lead_id)}
                        className="text-xs font-semibold text-blue-600 hover:underline"
                      >Retry GHL</button>
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">No leads yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
